/**
 * Text helpers shared by the chat render pipeline.
 *
 * These operate on a message body as a plain string, without knowing or caring
 * whether it will ultimately be rendered as legacy HTML or as Markdown.
 */

/**
 * Replace every non-newline character with a space, preserving line structure
 * and offsets so masked text can still be scanned positionally.
 */
function blankOut(text: string): string {
    return text.replace(/[^\n]/g, ' ');
}

const FENCE_LINE = /^\s*(`{3,}|~{3,})/;
const INLINE_CODE = /(`+)[^\n]*?\1/g;

/**
 * Blank out fenced code blocks and inline code spans, leaving everything else
 * (and the body's length and line count) intact.
 */
export function maskCodeRegions(body: string): string {
    if (!body) {
        return '';
    }

    let inFence = false;
    const masked = body
        .split('\n')
        .map((line) => {
            if (FENCE_LINE.test(line)) {
                inFence = !inFence;
                return blankOut(line);
            }
            return inFence ? blankOut(line) : line;
        })
        .join('\n');

    return masked.replace(INLINE_CODE, blankOut);
}

/**
 * Find the chat sound a body is asking to play, e.g. `[boing]`.
 *
 * @param body       Message body to scan.
 * @param soundNames Sound names available to this project.
 * @returns The canonical sound name (as it appears in soundNames), or false.
 */
export function findSoundToken(body: string, soundNames: string[]): string | false {
    if (!body || !soundNames || !soundNames.length) {
        return false;
    }

    const canonical = new Map<string, string>();
    soundNames.forEach((name) => {
        if (name) {
            canonical.set(String(name).toLowerCase(), String(name));
        }
    });

    // `(?!\()` skips Markdown link syntax: `[label](url)` is a link, not a sound.
    const token = /\[\s*([^\[\]\n]+?)\s*\](?!\()/g;
    const searchable = maskCodeRegions(body);

    let match: RegExpExecArray | null;
    while ((match = token.exec(searchable)) !== null) {
        const found = canonical.get(match[1].toLowerCase());
        if (found) {
            return found;
        }
    }

    return false;
}
